import React, { Component } from 'react'
import { withRouter } from 'react-router'
import { observer, inject } from 'mobx-react'
import { extendObservable, toJS, computed, action } from 'mobx'
import PropTypes from 'prop-types'
import { Form } from 'formik'
import moment from 'moment'
import get from 'lodash/get'


import CostItem from './CostItem'
import DeliverableFormGroup from './DeliverableFormGroup'
import FeeItem from './FeeItem'
import ReportingFormGroup from './ReportingFormGroup'
import Attachment from '@components/Attachment'
import Button from '@components/Button'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import { capi, fileApi } from '@atheneum/api'

class FeeFormBody extends Component {
  state = {
    capiInvoice: null,
    isCapiInvoice: false,
    isCapiCallOnlyInvoice: false,
    isCapiLopProjectType: false,
    isLopWithInvoicingEntity: false,
  }
  static propTypes = {
    modalStore: PropTypes.object,
    ruleStore: PropTypes.object,
    feeFormStore: PropTypes.object,
    feeStore: PropTypes.object,
    form: PropTypes.object.isRequired,
    formError: PropTypes.string
  }

  static defaultProps = {
    epl: true,
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: true
  }

  componentDidMount() {
    this.getCapiInvoice()
    this.setIsCapiLopProjectType()
  }

  setIsCapiLopProjectType() {
    const capiProjectType = get(this.props.feeFormStore.project, 'capiProjectType')
    const isLopWithInvoicingEntity = this.props.feeFormStore.project.caseCode
      && this.props.feeFormStore.project.invoicingEntityId
    const isCapiLopProjectType = capiProjectType === 'LOP'
    this.setState({
      isLopWithInvoicingEntity,
      isCapiLopProjectType
    })

    if (isCapiLopProjectType) {
      this.freeCall = true
      const { FEE_TYPES } = this.props.staticData.CONST
      const proposalId = this.props.staticData.feeTypes.find(
        feeType => feeType.id === FEE_TYPES.PROPOSAL_DISCOUNT_FEE
      ).id

      this.props.form.setFieldValue('feeTypeId', proposalId, false)
      this.props.form.setFieldTouched('feeTypeId', true)
    }
  }

  async getCapiInvoice() {
    const invoiceId = get(this.props.feeFormStore.fee, 'invoiceId')
    if (!invoiceId) return null

    const capiInvoice = await capi.checkInvoice({
      invoiceId
    })

    if (capiInvoice &&
      capiInvoice.platform_invoice_id) {
      this.setState({
        isCapiInvoice: invoiceId === parseInt(capiInvoice.platform_invoice_id),
        isCapiCallOnlyInvoice: capiInvoice.call_only_invoice,
        capiInvoice,
      })
    } else {
      this.setState({ isCapiInvoice: false, isCapiCallOnlyInvoice: false })
    }
  }


  constructor(props) {
    super(props)
    const { ruleStore, feeFormStore, feeStore } = props

    let maxDeliveryDate
    if (feeFormStore.fee.type === 'FEECOST' &&
      !(feeStore.eplModel && feeStore.eplModel.epl) &&
      !(feeFormStore.fee && feeFormStore.fee.eplId)) {
      maxDeliveryDate =
        get(feeStore, 'eplModel.epl.dateInterviewed', null) ||
        get(feeStore, 'epl.dateInterviewed') ||
        moment().format('YYYY-MM-DD')
    } else {
      maxDeliveryDate = moment().format('YYYY-MM-DD')
    }

    extendObservable(this, {
      minDeliveryDate: computed(() => {
        if (ruleStore.canSkipBackdating()) return null
        return ruleStore.getBackdatingStartDate()
      }),
      maxDeliveryDate,
      isExpertInterviewed: null,
      freeCall: !!feeFormStore.fee.freeCallFeeId
    })
  }

  isBackdatingRuleSatisfied = action(form => {
    // if fee wasn't created hold off with the check
    if (!this.props.feeFormStore.fee.id) return true

    const { ruleStore } = this.props
    const deliveryDate = form.values.deliveryDate
    if (!deliveryDate) return true

    return ruleStore.isBackdatingRuleSatisfied(deliveryDate)
  })

  getTitle(form) {
    const type = form.values.type
    return `${form.values.id ? 'Edit ' : 'Create new'}
      ${type === 'FEE' ? 'Client Charge' : ''}
      ${type === 'COST' ? 'Cost' : ''}
      ${type === 'FEECOST' ? 'Hon. & Fee Bundle' : ''}`
  }

  renderBackdatingRuleWarning(form) {
    if (this.isBackdatingRuleSatisfied(form)) return null

    const doesFeeExist = form.values.id
    const warningMessage = doesFeeExist
      ? 'Editing this fee is not allowed any more. Please consult with finance department about further action.'
      : 'Creating this fee is no longer allowed because the delivery date has already passed. Please make a backdating request to Ops Controlling.'

    return (
      <div className="p-b-md">
        <div className="hr-separator" />
        <InfoBox theme="red">Warning: {warningMessage}</InfoBox>
      </div>
    )
  }

  renderENSNotice = (callOnly) => {
    return (
      <div className="m-b-lg">
        <div className="hr-separator" />
        <InfoBox theme="green">
          This fee has been associated with an ENS {callOnly && <strong>call only</strong>} invoice.
        </InfoBox>
      </div>
    )
  }

  renderAssociatedInvoiceWarning = form => {
    if (!get(this.props.feeFormStore.fee, 'invoiceId')) return null
    if (this.state.capiInvoice && this.state.capiInvoice.status === 'draft') {
      return (
        <div className="m-b-lg">
          <div className="hr-separator" />
          <InfoBox theme="blue">
            The fee has been associated with an invoice, however, it can still be edited temporarily.
            Please note that once this invoice has been moved out of a draft status by finance, fees attached to it will no longer be editable.
          </InfoBox>
        </div>
      )
    }

    return (
      <div className="m-b-lg">
        <div className="hr-separator" />
        <InfoBox theme="red">
          The fee has been associated with the invoice, hence cannot be edited
          anymore. Please consult with finance department about further action.
        </InfoBox>
      </div>
    )
  }

  onFreeCallChange = _ => {
    const { FEE_TYPES } = this.props.staticData.CONST
    this.freeCall = !this.freeCall
    const proposalId = this.props.staticData.feeTypes.find(
      feeType => feeType.id === FEE_TYPES.PROPOSAL_DISCOUNT_FEE
    ).id
    this.props.form.setFieldValue('feeTypeId', proposalId, false)
    this.props.form.setFieldTouched('feeTypeId', true)

    this.props.form.setFieldValue('freeCall', this.freeCall, false)
    this.props.form.setFieldTouched('freeCall', true)

    if (!this.freeCall) {
      this.props.form.setFieldValue('freeCallCostTypeId', null, false)
      this.props.form.setFieldValue('freeCallFeeId', null, false)
    }
  }

  render() {
    const { modalStore, form, formError, feeFormStore } = this.props
    const { type } = feeFormStore.fee

    return (
      <div className="expert-form">
        <h1 className="expert-form__title">{this.getTitle(form)}</h1>
        {formError ? (
          <InfoBox theme="red" icon={<WarningIcon />}>
            {formError}
          </InfoBox>
        ) : null}
        {this.renderBackdatingRuleWarning(form)}
        {this.renderAssociatedInvoiceWarning(form)}
        {this.state.isCapiInvoice && this.renderENSNotice(this.state.isCapiCallOnlyInvoice)}
        <Form onSubmit={form.handleSubmit}>
          <DeliverableFormGroup
            form={form}
            deliverables={toJS(feeFormStore.projectDeliverables)}
            isBackdatingRuleSatisfied={this.isBackdatingRuleSatisfied}
            onDeliverableAttachChange={this.onDeliverableAttachChange}
          />
          {type === 'FEE' || type === 'FEECOST' ? (
            <FeeItem
              onFreeCallChange={this.onFreeCallChange}
              isCapiLopProjectType={this.state.isCapiLopProjectType}
              isLopWithInvoicingEntity={this.state.isLopWithInvoicingEntity}
              freeCall={this.freeCall && type === 'FEECOST'}
              disabled={!this.isBackdatingRuleSatisfied(form)}
              minDeliveryDate={this.minDeliveryDate}
              maxDeliveryDate={this.maxDeliveryDate}
              form={form}
              feeFormStore={feeFormStore}
            />
          ) : null}
          {type === 'COST' || type === 'FEECOST' ? (
            <CostItem
              form={form}
              hasFee={feeFormStore.fee.type === 'FEECOST'}
              disabled={!this.isBackdatingRuleSatisfied(form)}
              minDeliveryDate={this.minDeliveryDate}
              maxDeliveryDate={this.maxDeliveryDate}
              feeFormStore={feeFormStore}
              freeCall={this.freeCall}
            />
          ) : null}
          <ReportingFormGroup
            form={form}
            feeFormStore={feeFormStore}
            disabled={!this.isBackdatingRuleSatisfied(form)}
            isExpertInterviewed={this.isExpertInterviewed}
          />
          <div className="expert-form__section">
            <Attachment
              attachments={form.values.attachments}
              tooltip="Click to add attachments"
              readOnly={!this.isBackdatingRuleSatisfied(form)}
              onOk={({ data }) => {
                const attachments = form.values.attachments
                attachments.push(data[0])
                form.setFieldValue('attachments', attachments, false)
                form.setFieldTouched('attachments', true)
              }}
              onDelete={fileId => {
                fileApi.deleteFile(fileId).then(() => {
                  const attachments = form.values.attachments.filter(
                    atch => atch.id !== fileId
                  )
                  form.setFieldValue('attachments', attachments, false)
                  form.setFieldTouched('attachments', true)
                })
              }}
              label="Add attachment"
              url="/api/file"
            />
          </div>
          <div className="expert-form__section">
            <Button
              primary
              submit
              data-cy="save-honorarium-fee-bundle-btn"
              disabled={!!form.isSubmitting}>
              {form.isSubmitting ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={modalStore.close} disabled={form.isSubmitting}>
              Cancel
            </Button>
          </div>
        </Form>
      </div>
    )
  }
}

export default withRouter(
  inject('modalStore', 'ruleStore', 'staticData')(observer(FeeFormBody))
)
